import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Modal, Form } from 'react-bootstrap';
import Select from 'react-select';
import { NotificationManager } from 'react-notifications';
import { constants } from '../../../common/constants/constants';
import { fileToBase64 } from '../../../../utils/fileToBase64';
import { oneLineSelectStyles } from '../../../common/shared/select.styled';
import Input from '../../../common/simple/input';
import Button from '../../../common/Button';
import Datepicker from '../../../common/DatepickerPortal';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { getBuildingTitle, fetchBuildingData } from '../../../store/buildings/actions';
import upload from '../../../images/newDesign/upload.svg';
import cross from '../../../images/newDesign/cross.svg';
import Pdf from '../../../images/pdf.svg';
import { AddExpenseModalStyled } from './SetExpense.styled';
import AddPaymentMethodReccurring from './AddPaymentMethodReccurring';
import AddPaymentMethodOneTime from './AddPaymentMethodOneTime';
import Confirmation from './Confirmation';
import {selectCurrentBuildingCurrency} from "../../../store/buildings/selectors";

const AddExpenseModal = ({ show, onModalHide, buildingID, vendors, expenseTypes, openVendorPopup }) => {
    const { t } = useTranslation();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const titleData = useSelector(state => state.buildings.titleData);
    const dispatch = useDispatch();
    const client = useAccessToken();

    const [saveBtnsDisabled, setSaveBtnsDisabled] = useState(true);
    // data states
    const currency = useSelector(selectCurrentBuildingCurrency);

    const [currentVendorName, setCurrentVendorName] = useState('');
    const [currentExpenseType, setCurrentExpenseType] = useState('');
    const [currentAmount, setCurrentAmount] = useState('');
    const [currentPaymentFrequency, setCurrentPaymentFrequency] = useState(constants.APIContract.expenses.recurring);
    const [currentDate, setCurrentDate] = useState();
    const [currentNumOfMonths, setCurrentNumOfMonths] = useState('');
    const [currentInvoiceNum, setCurrentInvoiceNum] = useState('');
    const [currentNote, setCurrentNote] = useState('');

    const [currentPaymentMethod, setCurrentPaymentMethod] = useState(constants.managerPaymentsOptions.bankCharge);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [numOfMonthsOptions, setNumOfMonthsOptions] = useState([]);
    const [confirmationDate, setConfirmationDate] = useState();
    const [addExpensePayload, setAddExpensePayload] = useState();
    const [addPaymentMethodPayload, setAddPaymentMethodPayload] = useState();
    // modal container management states
    const [showAddExpense, setShowAddExpense] = useState(true);
    const [showAddPaymentMethod, setShowAddPaymentMethod] = useState(false);
    const [choosePaymentMethod, setChoosePaymentMethod] = useState(true);
    const [showConfirmation, setShowConfirmation] = useState(false);

    useEffect(async () => {
        if (!titleData.translationKey) dispatch(getBuildingTitle({buildingID}));

        const numArr = [...Array(12).keys()];
        let months = [];
        numArr.map(num => {
            months.push({
                value: num+1,
                label: num+1
            });
            return months
        });
        setNumOfMonthsOptions(months);
    }, []);

    useEffect(() => {
        if (currentVendorName.value === constants.expenses.addNewVendor) openVendorPopup()
    }, [currentVendorName]);

    const onVendorNameDropdownChange = e => setCurrentVendorName(e);
    const onExpenseTypeDropdownChange = e => setCurrentExpenseType(e);
    const onAmountInputChange = e => setCurrentAmount(e.target.value);
    const onPaymentFrequencyRadioChange = e => setCurrentPaymentFrequency(e.target.value);
    const onNumOfMonthsDropdownChange = e => setCurrentNumOfMonths(e);
    const onDatePickerChange = date => {
        setCurrentDate(date);
        setConfirmationDate(date);
    };
    const onInvoiceNumInputChange = e => setCurrentInvoiceNum(e.target.value);
    const onNoteInputChange = e => setCurrentNote(e.target.value);

    const onUploadFiles = async (e) => {
        const file = e.target.files[0];
        const base64 = await fileToBase64(file);
        let url;
        if (file.type.includes('image/')) url = URL.createObjectURL(file);
        else if (file.type.includes('application/pdf')) url=Pdf
        setUploadedFiles([...uploadedFiles, {name: file.name, url, base64}]);
    };

    const onImageDelete = i => {
        let images = uploadedFiles.slice();
        images.splice(i, 1);
        setUploadedFiles(images);
    };

    const onHide = () => {
        onModalHide();
        setShowAddExpense(true);
        setShowAddPaymentMethod(false);
        setChoosePaymentMethod(true);
        setShowConfirmation(false);
        setCurrentPaymentMethod(constants.managerPaymentsOptions.bankCharge);
        setCurrentVendorName('');
        setCurrentExpenseType('');
        setCurrentAmount('');
        setCurrentDate('');
        setCurrentNumOfMonths('');
        setCurrentInvoiceNum('');
        setCurrentNote('');
        setUploadedFiles([]);
    };

    useEffect(() => {
        if (currentPaymentFrequency === constants.APIContract.expenses.recurring && !currentNumOfMonths) setSaveBtnsDisabled(true)
        else if (currentAmount && currentExpenseType && currentVendorName && currentVendorName.value !== constants.expenses.addNewVendor && currentDate) setSaveBtnsDisabled(false)
        else setSaveBtnsDisabled(true)
    }, [currentAmount, currentExpenseType, currentVendorName, currentDate, currentPaymentFrequency, currentNumOfMonths]);

    const onSaveAndAddMethodClick = async (saveAndClose) => {
        dispatch(showLoader());

        let files = [];
        uploadedFiles.map(file => {
            const parsedFile = {fileName: file.name, fileBase64: file.base64};
            files.push(parsedFile);
            return files;
        });

        const payload = {
            buildingID,
            frequency: currentPaymentFrequency,
            numberOfMonths: currentPaymentFrequency === constants.APIContract.expenses.recurring ? currentNumOfMonths?.value : 1,
            // https://bllink.atlassian.net/browse/SSB-1324 error when trying to update a payment for vendor
            amount: parseFloat(currentAmount),
            invoiceNumber: currentInvoiceNum,
            plan: {
              description: currentNote,
              expenseTypeID: currentExpenseType.value,
              files: files
            },
            paymentDate: {
              month: currentDate.getMonth()+1,
              year: currentDate.getFullYear(),
              day: currentDate.getDate()
            },
            vendorID: currentVendorName.value
        };

        if (saveAndClose) {
            const response = await client.managers.expenses.addExpense(payload);
            if (response.success) {
                setShowAddExpense(false);
                onHide();
                NotificationManager.success(t('expenses.set_expenses.setExpenseSuccess'))
            } else NotificationManager.error(t('buildings.something_wrong'))
        } else {
            setAddExpensePayload(payload);
            setShowAddExpense(false);
            setShowAddPaymentMethod(true);
        }

        dispatch(hideLoader());
    };

    const onPaymentMethodChange = method => setCurrentPaymentMethod(method);

    return (
        <AddExpenseModalStyled
            show={show}
            onHide={onHide}
            centered
            size='lg'
            ltr={ltr}
        >
            <span
                className={'modal-close'}
                onClick={onHide}
            >
                &#215;
            </span>
            <div className='set-expense-title'>
                {showAddExpense && !showAddPaymentMethod && t('expenses.set_expenses.add_expense_for')}
                {currentPaymentFrequency === constants.APIContract.expenses.recurring && !showAddExpense && showAddPaymentMethod && !showConfirmation && t('expenses.set_expenses.add_payment_method')}
                {(showAddExpense || (currentPaymentFrequency === constants.APIContract.expenses.recurring && !showAddExpense && showAddPaymentMethod && !showConfirmation)) && titleData.translationKey && (
                    <span className='building-title'>{t('payments.' + titleData.translationKey, titleData)}</span>
                )}
                {currentPaymentFrequency === constants.APIContract.expenses.oneTime && !showAddExpense && showAddPaymentMethod && !showConfirmation && t('expenses.set_expenses.pay_with_bllink_or_record_offline')}
                {showConfirmation && (
                    <>
                        {t('expenses.set_expenses.confirmation_title')}
                        {titleData.translationKey && (
                            <span className='building-title'>{t('payments.' + titleData.translationKey, titleData)}</span>
                        )}
                    </>
                )}
            </div>

            <Modal.Body>
                {showAddExpense && !showAddPaymentMethod && <div className='modal-wrapper'>
                    <div className='dropdown-wrapper margin'>
                        <label>{t('expenses.name_of_supplier')}</label>
                        <Select
                            styles={oneLineSelectStyles}
                            isRtl={!ltr}
                            className='dropdown'
                            placeholder={t('expenses.set_expenses.search_vendor')}
                            options={vendors}
                            value={currentVendorName}
                            onChange={onVendorNameDropdownChange}
                        />
                    </div>

                    <div className='dropdown-wrapper margin'>
                        <label>{t('expenses.expense_type')}</label>
                        <Select
                            styles={oneLineSelectStyles}
                            isRtl={!ltr}
                            placeholder={t('expenses.set_expenses.choose_type')}
                            options={expenseTypes}
                            value={currentExpenseType}
                            onChange={onExpenseTypeDropdownChange}
                        />
                    </div>

                    <Input
                        label={t('payment_settings.amountHeader')}
                        value={currentAmount}
                        onChange={onAmountInputChange}
                        placeholder={`0.00 ${t(`currency.${currency}`)}`}
                    />

                    <div className='frequency-months-wrapper'>
                        <div className='frequency'>
                            <label className='label'>{t('expenses.payment_frequency')}</label>
                            <Form className='frequency-form'>
                                <Form.Check
                                    id="oneTime"
                                    type="radio"
                                    value={constants.APIContract.expenses.oneTime}
                                    name="paymentFrequency"
                                    label={t('reports.oneTime')}
                                    onChange={onPaymentFrequencyRadioChange}
                                    checked={currentPaymentFrequency === constants.APIContract.expenses.oneTime}
                                />
                                <Form.Check
                                    id="recurring"
                                    type="radio"
                                    value={constants.APIContract.expenses.recurring}
                                    name="paymentFrequency"
                                    label={t('reports.recurring')}
                                    onChange={onPaymentFrequencyRadioChange}
                                    checked={currentPaymentFrequency === constants.APIContract.expenses.recurring}
                                />
                            </Form>
                        </div>
                        <div className='dropdown-wrapper'>
                            {currentPaymentFrequency === constants.APIContract.expenses.recurring && (
                                <>
                                    <label className='num-of-months-label'>{t('expenses.set_expenses.num_of_months')}</label>
                                    <Select
                                        styles={oneLineSelectStyles}
                                        isRtl={!ltr}
                                        placeholder={t('expenses.set_expenses.select_months_placeholder')}
                                        options={numOfMonthsOptions}
                                        value={currentNumOfMonths}
                                        onChange={onNumOfMonthsDropdownChange}
                                    />
                                </>
                            )}
                        </div>
                    </div>

                    <div className='datepicker-wrapper'>
                        <label>{t('offline_payments.payment_date')}</label>
                        <Datepicker
                            date={currentDate}
                            onChange={onDatePickerChange}
                            className='one-line-design'
                            placeholder={t('expenses.set_expenses.select_date')}
                            ltr={ltr}
                            withPortal={false}
                            showArrow={false}
                            container
                        />
                    </div>

                    <div className='invoice-wrapper'>
                        <Input
                            label={t('expenses.expense_record_table_invoice')}
                            value={currentInvoiceNum}
                            onChange={onInvoiceNumInputChange}
                        />
                        <label className='upload-invoice' htmlFor='upload-files'>
                            <input id='upload-files' type="file" onChange={onUploadFiles} />
                            <div className='label'>{t('expenses.set_expenses.upload_invoice_file')}</div>
                            <img src={upload} className='upload' />
                        </label>
                    </div>

                    {uploadedFiles.length > 0 && <div className='uploaded-files'>
                        {uploadedFiles.map((file, i) => {
                            return (
                                <div key={i}>
                                    <div className='uploaded-image' onClick={() => onImageDelete(i)}>
                                        <img src={file.url} className='image'/>
                                        <div className='overlay'><img src={cross}/></div>
                                    </div>
                                    <div className='file-name'>{file.name}</div>
                                </div>
                            )
                        })}
                    </div>}

                    <Input
                        label={t('reports.note')}
                        value={currentNote}
                        onChange={onNoteInputChange}
                    />

                    <div className='button-wrapper'>
                        <Button
                            type='button'
                            onClick={() => onSaveAndAddMethodClick(false)}
                            rounded={false}
                            disabled={saveBtnsDisabled}
                        >
                            {t('expenses.set_expenses.save_and_add_method')}
                        </Button>
                        {currentPaymentFrequency === constants.APIContract.expenses.oneTime && (
                            <Button
                                type='button'
                                onClick={() => onSaveAndAddMethodClick(true)}
                                rounded={false}
                                outlined
                                disabled={saveBtnsDisabled}
                            >
                                {t('expenses.set_expenses.save_and_close')}
                            </Button>
                        )}
                    </div>
                </div>}

                {!showAddExpense && showAddPaymentMethod && currentPaymentFrequency === constants.APIContract.expenses.recurring && !showConfirmation && (
                    <AddPaymentMethodReccurring
                        monthsNum={currentNumOfMonths?.label}
                        amount={currentAmount}
                        currency={currency}
                        vendorName={currentVendorName?.label}
                        choosePaymentMethod={choosePaymentMethod}
                        setChoosePaymentMethod={setChoosePaymentMethod}
                        vendor={currentVendorName}
                        buildingID={buildingID}
                        setShowConfirmation={setShowConfirmation}
                        date={currentDate}
                        setConfirmationDate={setConfirmationDate}
                        setShowAddExpense={setShowAddExpense}
                        setShowAddPaymentMethod={setShowAddPaymentMethod}
                        setAddPaymentMethodPayload={setAddPaymentMethodPayload}
                    />
                )}

                {!showAddExpense && showAddPaymentMethod && currentPaymentFrequency === constants.APIContract.expenses.oneTime && !showConfirmation && (
                    <AddPaymentMethodOneTime
                        amount={currentAmount}
                        currency={currency}
                        vendorName={currentVendorName?.label}
                        choosePaymentMethod={choosePaymentMethod}
                        setChoosePaymentMethod={setChoosePaymentMethod}
                        paymentMethod={currentPaymentMethod}
                        onMethodChange={onPaymentMethodChange}
                        vendor={currentVendorName}
                        buildingID={buildingID}
                        setShowConfirmation={setShowConfirmation}
                        date={currentDate}
                        setConfirmationDate={setConfirmationDate}
                        setShowAddExpense={setShowAddExpense}
                        setShowAddPaymentMethod={setShowAddPaymentMethod}
                        setAddPaymentMethodPayload={setAddPaymentMethodPayload}
                    />
                )}

                {showConfirmation && (
                    <Confirmation
                        vendorName={currentVendorName.label}
                        amount={currentAmount}
                        paymentMethod={currentPaymentMethod}
                        currency={currency}
                        onHide={onHide}
                        date={confirmationDate}
                        addExpensePayload={addExpensePayload}
                        addPaymentMethodPayload={addPaymentMethodPayload}
                        setShowConfirmation={setShowConfirmation}
                        setShowAddExpense={setShowAddExpense}
                        setShowAddPaymentMethod={setShowAddPaymentMethod}
                        setChoosePaymentMethod={setChoosePaymentMethod}
                    />
                )}
            </Modal.Body>
        </AddExpenseModalStyled>
    )
}

export default AddExpenseModal;
